import * as React from 'react';
import { useState } from 'react';
import { observer } from 'mobx-react';
import cn from 'classnames';

import { TableChunkModel } from 'store/models/chunks/table/TableChunkModel';
import SimpleTextEditor from 'components/common/TextEditor/SimpleTextEditor/SimpleTextEditor';
import InlineFormatToolbar from 'components/common/TextEditor/InlineFormatToolbar';
import { sanitize } from 'components/common/TextEditor/utils';
import ButtonPlus from 'components/common/Button/ButtonPlus/ButtonPlus';
import TrashIcon from 'components/icons/TrashIcon';

import { WithChunk } from '../types';

import styles from './TableChunk.modules.scss';

type Props = WithChunk<TableChunkModel>;

const TableChunk: React.FC<Props> = ({ chunk, isEditMode }: Props) => {
  const { data } = chunk;
  const [focusedCell, setFocusedCell] = useState<string | null>(null);

  if (!isEditMode) {
    return (
      <table className={styles.table}>
        <tbody>
          {data.rows.map((row, rowIndex) => {
            const CellTag = data.hasHeaderRow && rowIndex === 0 ? 'th' : 'td';
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <CellTag
                    // eslint-disable-next-line react/no-array-index-key
                    key={colIndex}
                    className={cn(
                      styles.cell,
                      data.hasHeaderRow && rowIndex === 0 && styles.headerCell
                    )}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: sanitize(cell) }}
                  />
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <div className={styles.root}>
      <label className={styles.headerToggle}>
        <input
          type="checkbox"
          checked={data.hasHeaderRow}
          onChange={data.toggleHeaderRow}
        />
        Первая строка — шапка
      </label>

      <table className={styles.table}>
        <tbody>
          {data.rows.map((row, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex}>
              {row.map((cell, colIndex) => {
                const cellKey = `${rowIndex}:${colIndex}`;
                return (
                  <td
                    // eslint-disable-next-line react/no-array-index-key
                    key={colIndex}
                    className={cn(
                      styles.cell,
                      data.hasHeaderRow && rowIndex === 0 && styles.headerCell
                    )}
                  >
                    {focusedCell === cellKey && <InlineFormatToolbar />}
                    <SimpleTextEditor
                      text={cell}
                      isEditMode
                      onChange={(html) =>
                        data.setCell(rowIndex, colIndex, html)
                      }
                      onFocus={() => setFocusedCell(cellKey)}
                      onBlur={() =>
                        setFocusedCell((current) =>
                          current === cellKey ? null : current
                        )
                      }
                      className={styles.cellEditor}
                    />
                  </td>
                );
              })}
              {data.rows.length > 1 && (
                <td className={styles.controlCell}>
                  <button
                    type="button"
                    className={styles.iconButton}
                    title="Удалить строку"
                    onClick={() => data.removeRow(rowIndex)}
                  >
                    <TrashIcon className={styles.trashIcon} />
                  </button>
                </td>
              )}
            </tr>
          ))}
          {(data.rows[0]?.length || 0) > 1 && (
            <tr>
              {data.rows[0].map((_, colIndex) => (
                <td
                  // eslint-disable-next-line react/no-array-index-key
                  key={colIndex}
                  className={styles.controlCell}
                >
                  <button
                    type="button"
                    className={styles.iconButton}
                    title="Удалить столбец"
                    onClick={() => data.removeColumn(colIndex)}
                  >
                    <TrashIcon className={styles.trashIcon} />
                  </button>
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>

      <div className={styles.addButtons}>
        <ButtonPlus onClick={data.addRow}>Добавить строку</ButtonPlus>
        <ButtonPlus onClick={data.addColumn}>Добавить столбец</ButtonPlus>
      </div>
    </div>
  );
};

export default observer(TableChunk);
